import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, Loader2, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useSignupRedirectSettings, useUpdateSignupRedirectSettings } from '@/hooks/settings/useSignupRedirectSettings';

type SignupRedirectFormValues = {
  enabled: boolean;
  url: string;
  verificationEnabled: boolean;
  verificationUrl: string;
};

function isValidUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return Boolean(parsed.protocol && parsed.hostname);
  } catch {
    return false;
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object'
  ) {
    const response = error.response as { data?: { message?: string } };
    if (response.data?.message) {
      return response.data.message;
    }
  }
  return fallback;
}

export function SignupRedirectSettings() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useSignupRedirectSettings();
  const updateSettings = useUpdateSignupRedirectSettings();

  const schema = useMemo(
    () =>
      z
        .object({
          enabled: z.boolean(),
          url: z
            .string()
            .trim()
            .max(2048, { message: t('settings.signupRedirect.validation.max') })
            .optional(),
          verificationEnabled: z.boolean(),
          verificationUrl: z
            .string()
            .trim()
            .max(2048, { message: t('settings.signupRedirect.validation.max') })
            .optional(),
        })
        .superRefine((values, ctx) => {
          const trimmedUrl = values.url?.trim() ?? '';
          const trimmedVerificationUrl = values.verificationUrl?.trim() ?? '';
          if (values.enabled) {
            if (!trimmedUrl) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('settings.signupRedirect.validation.required'),
                path: ['url'],
              });
              return;
            }
            if (!isValidUrl(trimmedUrl)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('settings.signupRedirect.validation.url'),
                path: ['url'],
              });
            }
          } else if (trimmedUrl && !isValidUrl(trimmedUrl)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('settings.signupRedirect.validation.url'),
              path: ['url'],
            });
          }

          if (values.verificationEnabled) {
            if (!trimmedVerificationUrl) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('settings.signupRedirect.validation.required'),
                path: ['verificationUrl'],
              });
              return;
            }
            if (!isValidUrl(trimmedVerificationUrl)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('settings.signupRedirect.validation.url'),
                path: ['verificationUrl'],
              });
            }
          } else if (trimmedVerificationUrl && !isValidUrl(trimmedVerificationUrl)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('settings.signupRedirect.validation.url'),
              path: ['verificationUrl'],
            });
          }
        }),
    [t],
  );

  const form = useForm<SignupRedirectFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      enabled: false,
      url: '',
      verificationEnabled: false,
      verificationUrl: '',
    },
  });

  useEffect(() => {
    if (data) {
      form.reset({
        enabled: data.signup?.enabled ?? data.enabled,
        url: data.signup?.url ?? data.url ?? '',
        verificationEnabled: data.verification?.enabled ?? false,
        verificationUrl: data.verification?.url ?? '',
      });
    }
  }, [data, form]);

  const isSubmitting = updateSettings.isPending;
  const watchEnabled = form.watch('enabled');
  const watchVerificationEnabled = form.watch('verificationEnabled');

  const onSubmit = async (values: SignupRedirectFormValues) => {
    try {
      form.clearErrors('root');
      await updateSettings.mutateAsync({
        enabled: values.enabled,
        url: values.url?.trim() || null,
        verificationEnabled: values.verificationEnabled,
        verificationUrl: values.verificationUrl?.trim() || null,
      });
      toast.success(t('settings.signupRedirect.messages.saveSuccess'));
    } catch (error) {
      const message = getErrorMessage(error, t('settings.signupRedirect.messages.saveError'));
      form.setError('root', { type: 'server', message });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            {t('settings.sections.signupRedirect.title')}
          </CardTitle>
          <CardDescription>{t('settings.signupRedirect.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            {t('settings.sections.signupRedirect.title')}
          </CardTitle>
          <CardDescription>{t('settings.signupRedirect.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{t('settings.signupRedirect.messages.loadError')}</AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter>
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('settings.signupRedirect.actions.retry')}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          {t('settings.sections.signupRedirect.title')}
        </CardTitle>
        <CardDescription>{t('settings.signupRedirect.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div className="space-y-2">
                <FormLabel className="text-sm font-semibold">
                  {t('settings.signupRedirect.sections.signup')}
                </FormLabel>
                <FormDescription className="text-muted-foreground">
                  {t('settings.signupRedirect.helpers.enabled')}
                </FormDescription>
              </div>
              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                    <div className="space-y-1">
                      <FormLabel className="text-base">{t('settings.signupRedirect.fields.enabled')}</FormLabel>
                      <FormDescription className="max-w-2xl">
                        {t('settings.signupRedirect.helpers.enabled')}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSubmitting} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.signupRedirect.fields.url')}</FormLabel>
                    <FormDescription>{t('settings.signupRedirect.helpers.url')}</FormDescription>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={isSubmitting || !watchEnabled}
                        placeholder={t('settings.signupRedirect.placeholders.url')}
                        inputMode="url"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <FormLabel className="text-sm font-semibold">
                  {t('settings.signupRedirect.sections.verification')}
                </FormLabel>
                <FormDescription className="text-muted-foreground">
                  {t('settings.signupRedirect.helpers.verification')}
                </FormDescription>
              </div>
              <FormField
                control={form.control}
                name="verificationEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                    <div className="space-y-1">
                      <FormLabel className="text-base">
                        {t('settings.signupRedirect.fields.verificationEnabled')}
                      </FormLabel>
                      <FormDescription className="max-w-2xl">
                        {t('settings.signupRedirect.helpers.verificationToggle')}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSubmitting} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="verificationUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.signupRedirect.fields.verificationUrl')}</FormLabel>
                    <FormDescription>{t('settings.signupRedirect.helpers.verificationUrl')}</FormDescription>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={isSubmitting || !watchVerificationEnabled}
                        placeholder={t('settings.signupRedirect.placeholders.verificationUrl')}
                        inputMode="url"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {form.formState.errors.root?.message && (
              <Alert variant="destructive">
                <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
              </Alert>
            )}

            <Alert>
              <AlertDescription>{t('settings.signupRedirect.helpers.scope')}</AlertDescription>
            </Alert>

            <CardFooter className="px-0">
              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isSubmitting ? t('settings.signupRedirect.actions.saving') : t('settings.signupRedirect.actions.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (data) {
                      form.reset({
                        enabled: data.signup?.enabled ?? data.enabled,
                        url: data.signup?.url ?? data.url ?? '',
                        verificationEnabled: data.verification?.enabled ?? false,
                        verificationUrl: data.verification?.url ?? '',
                      });
                    } else {
                      form.reset({
                        enabled: false,
                        url: '',
                        verificationEnabled: false,
                        verificationUrl: '',
                      });
                    }
                  }}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t('settings.signupRedirect.actions.retry')}
                </Button>
              </div>
            </CardFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
